import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, Users, LayoutDashboard, Menu, X, FileText, Package, Database, LogIn, LogOut, Inbox, Tag, Download, Share, Plus, Warehouse, Hash, ChevronDown, ChevronRight, FolderOpen, Trash2, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [archiviOpen, setArchiviOpen] = useState(true);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    // Controlla se l'app è già installata
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone === true;
    setIsInstalled(isStandalone);

    // Ascolta evento beforeinstallprompt per PWA (Android/Desktop)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  const navigation = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Bins', path: '/bins', icon: Box },
    { type: 'collapsible', label: 'ARCHIVI', icon: FolderOpen, key: 'archivi', items: [
      { name: 'Fornitori', path: '/fornitori', icon: Users },
      { name: 'ID', path: '/id-fornitori', icon: Hash },
      { name: 'Prodotti', path: '/prodotti', icon: Tag },
    ]},
    { name: 'Movimenti', path: '/movimenti', icon: FileText },
    { name: 'Disponibilità', path: '/storico', icon: Package },
    { name: 'Da Importare', path: '/da-importare', icon: Inbox },
    { type: 'divider', label: 'CELLA (No Depurazione)' },
    { name: 'Cella', path: '/cella', icon: Warehouse },
    { name: 'Movimenti Cella', path: '/cella/movimenti', icon: FileText },
    { type: 'divider', label: 'SISTEMA' },
    { name: 'Backup', path: '/backup', icon: Database },
    { name: 'Operatore IN', path: '/operatore-in', icon: LogIn },
    { name: 'Operatore OUT', path: '/operatore-out', icon: LogOut },
  ];

  const handleReset = async () => {
    if (!resetPassword) {
      toast.error('Inserisci la password');
      return;
    }
    
    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/sistema/reset-completo`, {
        password: resetPassword
      });
      
      toast.success('Reset completato!');
      toast.info(`Eliminati: ${response.data.eliminati.bins} bins, ${response.data.eliminati.movimenti} movimenti`);
      setShowResetModal(false);
      setResetPassword('');
      
      // Ricarica la pagina per aggiornare i dati
      window.location.reload();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Password errata!');
      } else {
        toast.error(error.response?.data?.detail || 'Errore durante il reset');
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1628] via-[#0F1E32] to-[#1A2D4A]">
      {/* Modal Guida Installazione */}
      {showInstallGuide && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl">
            <div className="bg-[#FFE500] p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img 
                  src="/icons/logo-blumarlin.png" 
                  alt="Blu Marlin" 
                  className="w-12 h-12 object-contain rounded-lg"
                />
                <div>
                  <h3 className="font-bold text-[#0A1628]">Installa l'App</h3>
                  <p className="text-xs text-[#0A1628]/70">Blu Marlin Bivalvi</p>
                </div>
              </div>
              <button 
                onClick={() => setShowInstallGuide(false)}
                className="p-2 hover:bg-black/10 rounded-full"
              >
                <X className="w-5 h-5 text-[#0A1628]" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {(isAndroid || (!isIOS && !isAndroid)) && deferredPrompt ? (
                <>
                  <p className="text-sm text-gray-600 text-center">
                    Installa l'app direttamente sul tuo dispositivo per un accesso rapido!
                  </p>
                  <button
                    onClick={handleInstall}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#0A1628] text-[#FFE500] rounded-xl font-bold uppercase"
                  >
                    <Download className="w-5 h-5" />
                    Installa Ora
                  </button>
                </>
              ) : isIOS ? (
                <>
                  <p className="text-sm text-gray-600 text-center mb-4">
                    Per installare l'app su iPhone/iPad segui questi passaggi:
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0">
                        <Share className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-[#0A1628]">1. Tocca "Condividi"</p>
                        <p className="text-xs text-gray-500">L'icona con la freccia in basso nella barra di Safari</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 bg-[#0A1628] rounded-full flex items-center justify-center flex-shrink-0">
                        <Plus className="w-4 h-4 text-[#FFE500]" />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-[#0A1628]">2. "Aggiungi a Home"</p>
                        <p className="text-xs text-gray-500">Scorri e tocca "Aggiungi a schermata Home"</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 bg-[#FFE500] rounded-full flex items-center justify-center flex-shrink-0">
                        <img src="/icons/icon-72x72.png" alt="" className="w-6 h-6 rounded" />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-[#0A1628]">3. Conferma</p>
                        <p className="text-xs text-gray-500">Tocca "Aggiungi" in alto a destra</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-600 text-center">
                    Per installare l'app, usa il menu del browser (tre puntini) e seleziona "Installa app" o "Aggiungi a schermata Home"
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 bg-[#0A1628] rounded-full flex items-center justify-center flex-shrink-0">
                        <Menu className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-[#0A1628]">1. Apri il menu del browser</p>
                        <p className="text-xs text-gray-500">Clicca sui tre puntini in alto a destra</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-8 h-8 bg-[#FFE500] rounded-full flex items-center justify-center flex-shrink-0">
                        <Download className="w-4 h-4 text-[#0A1628]" />
                      </div>
                      <div>
                        <p className="font-bold text-sm text-[#0A1628]">2. Installa l'app</p>
                        <p className="text-xs text-gray-500">Seleziona "Installa app" o "Installa Blu Marlin"</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className="px-6 pb-6">
              <button
                onClick={() => setShowInstallGuide(false)}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Header */}
      <header className="lg:hidden bg-[#FFE500] px-4 py-3 flex items-center justify-between sticky top-0 z-40 shadow-lg">
        <div className="flex items-center gap-3">
          <img 
            src="/icons/logo-blumarlin.png" 
            alt="Blu Marlin" 
            className="w-10 h-10 object-contain rounded-lg"
          />
          <div>
            <h1 className="text-sm font-bold text-[#0A1628] tracking-tight">BLU MARLIN</h1>
            <p className="text-[10px] text-[#0A1628]/70">BIVALVI S.R.L.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isInstalled && (
            <button
              onClick={() => setShowInstallGuide(true)}
              className="p-2 bg-[#0A1628] text-[#FFE500] rounded-lg"
              title="Installa App"
            >
              <Download className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-black/10 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-6 h-6 text-[#0A1628]" /> : <Menu className="w-6 h-6 text-[#0A1628]" />}
          </button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-72 bg-[#0A1628]/95 backdrop-blur-xl border-r border-white/10 z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-4">
            <img 
              src="/icons/logo-blumarlin.png" 
              alt="Blu Marlin Bivalvi" 
              className="w-14 h-14 object-contain rounded-xl shadow-lg"
            />
            <div>
              <h1 className="text-lg font-bold text-[#FFE500] tracking-tight">BLU MARLIN</h1>
              <p className="text-xs text-white/50">BIVALVI S.R.L.</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-200px)]">
          {navigation.map((item, index) => {
            if (item.type === 'divider') {
              return (
                <div key={index} className="py-2 mt-2">
                  <p className="px-4 text-[10px] font-bold text-[#8B5CF6]/70 uppercase tracking-wider">{item.label}</p>
                </div>
              );
            }
            
            // Sezione collassabile (ARCHIVI)
            if (item.type === 'collapsible') {
              const Icon = item.icon;
              const isAnyChildActive = item.items.some(subItem => location.pathname === subItem.path);
              return (
                <div key={index}>
                  <button
                    onClick={() => setArchiviOpen(!archiviOpen)}
                    className={`
                      w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium text-sm transition-all
                      ${isAnyChildActive 
                        ? 'bg-[#FFE500]/20 text-[#FFE500]' 
                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5" />
                      {item.label}
                    </div>
                    {archiviOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  {archiviOpen && (
                    <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-2">
                      {item.items.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = location.pathname === subItem.path;
                        return (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`
                              flex items-center gap-3 px-4 py-2 rounded-xl font-medium text-xs transition-all
                              ${isSubActive 
                                ? 'bg-[#FFE500] text-[#0A1628] shadow-lg shadow-[#FFE500]/20' 
                                : 'text-white/50 hover:bg-white/10 hover:text-white'
                              }
                            `}
                          >
                            <SubIcon className="w-4 h-4" />
                            {subItem.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-all
                  ${isActive 
                    ? 'bg-[#FFE500] text-[#0A1628] shadow-lg shadow-[#FFE500]/20' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Pulsante RESET */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={() => setShowResetModal(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600/20 border border-red-500/50 text-red-400 rounded-xl font-bold uppercase text-xs hover:bg-red-600/30 transition-colors"
            data-testid="reset-btn"
          >
            <Trash2 className="w-4 h-4" />
            Reset Sistema
          </button>
        </div>

        {/* Install Button - Desktop */}
        {!isInstalled && (
          <div className="p-4 border-t border-white/10">
            <button
              onClick={() => setShowInstallGuide(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#FFE500] text-[#0A1628] rounded-xl font-bold uppercase text-xs shadow-lg shadow-[#FFE500]/20 hover:bg-[#FFE500]/90 transition-colors"
              data-testid="install-app-btn"
            >
              <Download className="w-4 h-4" />
              Installa App
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <p className="text-xs text-white/30 text-center">Sistema Depurazione</p>
        </div>
      </aside>

      {/* Modal Reset */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
          <div className="bg-[#0F1E32] border border-red-500/50 rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl">
            <div className="bg-red-600/20 p-4 flex items-center gap-3 border-b border-red-500/30">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <h3 className="font-bold text-red-400 uppercase">Reset Sistema</h3>
                <p className="text-xs text-red-400/70">Questa azione è irreversibile</p>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="text-sm text-white mb-2">Verranno eliminati:</p>
                <ul className="text-xs text-red-400 space-y-1">
                  <li>• Tutti i Bins</li>
                  <li>• Tutti i Movimenti</li>
                  <li>• Tutte le Richieste di importazione</li>
                  <li>• Tutti i dati della Cella</li>
                </ul>
              </div>
              
              <div>
                <p className="text-sm text-white/70 mb-2 font-bold">Sei sicuro di voler confermare?</p>
                <p className="text-xs text-white/50 mb-3">Inserisci la password per procedere:</p>
                <input
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-red-500 text-center font-mono text-lg tracking-widest"
                  data-testid="reset-password-input"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowResetModal(false);
                    setResetPassword('');
                  }}
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-bold uppercase text-xs hover:bg-white/20 transition-colors"
                  data-testid="reset-cancel-btn"
                >
                  Annulla
                </button>
                <button
                  onClick={handleReset}
                  disabled={resetLoading}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-bold uppercase text-xs hover:bg-red-700 transition-colors disabled:opacity-50"
                  data-testid="reset-confirm-btn"
                >
                  {resetLoading ? 'Eliminazione...' : 'Elimina Tutto'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen">
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
